import { useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Priya Sharma",
    location: "Delhi",
    rating: 5,
    text: "CareCrony provided an amazing caregiver for my mother-in-law. The staff is courteous, professional, and truly caring. We feel so relieved knowing she's in safe hands.",
    service: "Elder Care",
  },
  {
    name: "Rajesh Kumar",
    location: "Mumbai",
    rating: 5,
    text: "After my father's knee surgery, we needed professional post-surgery care at home. CareCrony sent a trained nurse within hours. The recovery has been smooth and comfortable.",
    service: "Post-Surgery Care",
  },
  {
    name: "Anita Patel",
    location: "Bangalore",
    rating: 5,
    text: "We've been using their babysitting service for 3 months now. The caregiver is wonderful with our daughter. Background verified, punctual, and very loving. Highly recommended!",
    service: "Child Care",
  },
  {
    name: "Dr. Suresh Menon",
    location: "Hyderabad",
    rating: 5,
    text: "As a doctor, I'm particular about the quality of home nursing. CareCrony exceeded my expectations. Their nurses are well-trained and follow proper medical protocols.",
    service: "Home Nursing",
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);

  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 md:py-28 bg-gradient-to-br from-[#F0FDFA] to-[#F0F9FF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-[#14B8A6] uppercase tracking-wider font-body">
            Testimonials
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] tracking-tight mt-3">
            Loved by Families Across India
          </h2>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              data-testid={`testimonial-card-${i}`}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex flex-col"
            >
              <Quote className="w-8 h-8 text-[#14B8A6]/20 mb-4" strokeWidth={1.5} />
              <p className="text-sm leading-relaxed text-[#475569] font-body flex-1 mb-4">
                "{t.text}"
              </p>
              <div className="flex gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                ))}
              </div>
              <div>
                <p className="font-heading text-sm font-semibold text-[#0F172A]">{t.name}</p>
                <p className="text-xs text-[#475569] font-body">
                  {t.location} &middot; {t.service}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: Slider */}
        <div className="md:hidden">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <Quote className="w-8 h-8 text-[#0F766E]/20 mb-4" strokeWidth={1.5} />
            <p className="text-sm leading-relaxed text-[#475569] font-body mb-4">
              "{testimonials[current].text}"
            </p>
            <div className="flex gap-1 mb-3">
              {Array.from({ length: testimonials[current].rating }).map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
              ))}
            </div>
            <p className="font-heading text-sm font-semibold text-[#0F172A]">
              {testimonials[current].name}
            </p>
            <p className="text-xs text-[#475569] font-body">
              {testimonials[current].location} &middot; {testimonials[current].service}
            </p>
          </div>
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prev}
              data-testid="testimonial-prev"
              className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-[#F0FDFA] transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#0F172A]" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === current ? "bg-[#14B8A6]" : "bg-slate-300"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              data-testid="testimonial-next"
              className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-[#F0FDFA] transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#0F172A]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
